/* eslint-disable camelcase */
// the unused vars need to be there otherwise the component doesn't rerender on those property change
/* eslint-disable no-unused-vars */
import React from 'react';
import { observer } from 'mobx-react';
import {
  Card,
  HSpacer,
  Button,
  LoadingOverlay,
  ContentSnippet,
  Text,
  VSpacer,
  Icon,
  Tooltip,
} from '@mqd/volt-base';
import s from './BeneficialOwnersTab.module.css';
import { StoreContext } from '@mqd/volt-contexts';
import AddIdentificationModal from '../shared-components/add-identification-modal/AddIdentificationModal';
import PropTypes from 'prop-types';
import { ParentDetailCard } from '@mq/voltron-parent';
import BusinessDetailsHome from 'components/business-details/BusinessDetailsHome.js';
import BusinessDetailsBaseInfo from 'components/business-details/BusinessDetailsBaseInfo';

class BeneficialOwnerCard extends ParentDetailCard {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.state = {
      editActive: false,
      showAddIdentificationModal: false,
      loading: false,
    };
  }

  get businessStore() {
    const { businessStore } = this.props;
    return businessStore || {};
  }

  get beneficialOwner() {
    const { beneficialOwner = {} } = this.props;
    return beneficialOwner || {};
  }

  get cardTitle() {
    const { cardTitle } = this.props;
    return cardTitle || 'Beneficial Owner';
  }

  normalizeTitle(title) {
    return title.toLowerCase().replace(' ', '_').replace(' ', '');
  }

  async handleSave() {
    const { updateBusiness } = this.businessStore;
    this.setState({ loading: true });
    const success = await updateBusiness();
    if (success) {
      this.setState({ loading: false, editActive: false });
    } else {
      this.setState({ loading: false });
    }
  }

  handleCancel() {
    const { revertUpdates } = this.businessStore;
    revertUpdates && revertUpdates();
    this.setState({ editActive: false });
  }

  renderHome() {
    const { editActive } = this.state;
    const { home = {} } = this.beneficialOwner;

    return (
      <BusinessDetailsHome
        ownerType="beneficial owner"
        editActive={editActive}
        home={home}
        renderSnippet={this.renderSnippet}
      />
    );
  }
  renderAddIdentificationModal() {
    const { showAddIdentificationModal, loading } = this.state;
    const { addBeneficialOwnerIdentification } = this.businessStore;
    const { getIdentification, isEdit } = this.beneficialOwner;
    const beneficial = this.normalizeTitle(this.cardTitle);
    if (!showAddIdentificationModal) return null;
    return (
      <LoadingOverlay active={loading}>
        <AddIdentificationModal
          isEdit={isEdit}
          values={getIdentification}
          options={['SSN', 'SIN', 'TIN', 'NIN']}
          disableExpirationDateFor={['SSN', 'SIN', 'TIN', 'NIN']}
          handleSave={async ({ type, value, expirationDate }) => {
            this.setState({ loading: true, showAddIdentificationModal: false });
            addBeneficialOwnerIdentification &&
              (await addBeneficialOwnerIdentification({ type, value, expirationDate }, beneficial));
            this.setState({ loading: false });
          }}
          hideModal={() => {
            this.setState({ showAddIdentificationModal: false });
          }}
        />
      </LoadingOverlay>
    );
  }

  renderIdentifications() {
    const { canRevealIdentification, uamGranularPermissionsActive } = this.props;
    const { editActive } = this.state;
    const {
      revealIdentificationBeneficialOwner,
      revealIdentificationGranularBeneficialOwner,
      hideIdentificationBeneficialOwner,
    } = this.businessStore;
    const beneficial = this.normalizeTitle(this.cardTitle);
    let { identifications, isEdit } = this.beneficialOwner;
    if (!identifications) {
      identifications = [];
    }
    const snippets = identifications.map((identification, i) => {
      const { type, expiration_date, loading, valueWithStars } = identification;
      return (
        <LoadingOverlay active={loading} key={i}>
          <ContentSnippet title={type} key={i} horizontalLayout>
            <div className={s.spaceEvenly}>
              <Text>{valueWithStars}</Text>

              {this.renderSsnRevealButton({
                editActive,
                canReveal: canRevealIdentification,
                value: valueWithStars,
                reveal: () =>
                  uamGranularPermissionsActive
                    ? revealIdentificationGranularBeneficialOwner(type, beneficial)
                    : revealIdentificationBeneficialOwner(type, beneficial),
                hide: () => hideIdentificationBeneficialOwner(type, beneficial),
              })}
            </div>
            {expiration_date ? (
              <div className={s.idExpires}>
                <Text mod="light">Expires: {expiration_date}</Text>
              </div>
            ) : null}
          </ContentSnippet>
          <VSpacer factor={2} />
        </LoadingOverlay>
      );
    });
    return (
      <div data-testid="v2-identifications">
        <VSpacer factor={2} />
        <div className={s.flexDiv}>
          <Text type="h5">Identifications</Text>
          <HSpacer factor={0.5} />
          <div style={{ height: 22 }}>
            <Tooltip
              zIndex={9999}
              content={
                <div>
                  Forms of identification used to validate the Beneficial Owner.
                  <br />
                  <span style={{ fontWeight: 'bold' }}>
                    Required for verification (KYC) checks.
                  </span>
                  <br />
                  <br />
                  <span style={{ fontWeight: 'bold' }}>Note:</span> Only one of the following
                  identification types can be associated with a business:
                  <ul>
                    <li>SSN – Social Security Number</li>
                    <li>SIN – Social Insurance Number</li>
                    <li>TIN – Taxpayer Identification Number</li>
                    <li>NIN – National Insurance Number</li>
                  </ul>
                  <br />
                  <span style={{ fontWeight: 'bold' }}>Also Note:</span> SSN is required for KYC
                  verification checks.
                </div>
              }
            >
              <Icon type="info" mod="disabled" hasTooltip />
            </Tooltip>
          </div>
          <HSpacer factor={5} />
          <Button
            size="xs"
            type="outline"
            onClick={() => this.setState({ showAddIdentificationModal: true })}
            testId={`${isEdit ? 'Edit' : 'Add New'}_button`}
          >
            {isEdit ? 'Edit' : 'Add New'}
          </Button>
        </div>
        <VSpacer factor={3} />
        {snippets}
      </div>
    );
  }

  renderV1Identification() {
    const { editActive } = this.state;
    const { updateForSave, loading, ssnWithStars } = this.beneficialOwner;
    const beneficial = this.normalizeTitle(this.cardTitle);
    const { canRevealIdentification, uamGranularPermissionsActive } = this.props;
    const {
      revealV1IdentificationBeneficialOwner,
      revealV1IdentificationGranularBeneficialOwner,
      hideV1IdentificationBeneficialOwner,
    } = this.businessStore;
    return (
      <div data-testid="v1-identifications">
        <VSpacer factor={2} />
        <div className={s.spaceBetween}>
          <div className={s.flexDiv}>
            <Text type="h5">Identifications</Text>
            <HSpacer factor={0.5} />
            <div style={{ height: 22 }}>
              <Tooltip
                zIndex={9999}
                content={`
                    Forms of identification used to validate the beneficial owner.
                    Required for verification (KYC) checks. SSN needs to be 9 digits
                    Note: SSN is required for KYC
                    verification checks.
                  `}
              >
                <Icon type="info" mod="disabled" hasTooltip />
              </Tooltip>
            </div>
          </div>
          {this.renderSsnRevealButton({
            editActive,
            canReveal: canRevealIdentification,
            value: ssnWithStars,
            reveal: () =>
              uamGranularPermissionsActive
                ? revealV1IdentificationGranularBeneficialOwner(beneficial)
                : revealV1IdentificationBeneficialOwner(beneficial),
            hide: () => hideV1IdentificationBeneficialOwner(beneficial),
          })}
        </div>
        <VSpacer factor={2} />
        <LoadingOverlay active={loading}>
          {this.renderSnippet({
            title: 'SSN',
            content: ssnWithStars,
            editActive: editActive,
            onChange: (e) => updateForSave('ssn', e.target.value),
            tooltipContent: 'SSN of the beneficial owner.',
          })}
        </LoadingOverlay>
      </div>
    );
  }

  renderHeaderButtons() {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button type="outline" onClick={this.handleCancel}>
          Cancel
        </Button>
        <HSpacer />
        <Button onClick={this.handleSave}>Save</Button>
      </div>
    );
  }

  renderBaseInfo() {
    const { editActive } = this.state;

    return (
      <BusinessDetailsBaseInfo
        ownerType="beneficial owner"
        editActive={editActive}
        person={this.beneficialOwner}
        renderSnippet={this.renderSnippet}
      />
    );
  }

  render() {
    const { loading } = this.state;
    const { editable } = this.props;
    const { editActive } = this.state;
    const testId = `${this.cardTitle.replace(/\s+/g, '-').toLowerCase()}-card`;
    const { ssn, loading: beneficialOwnerLoading, identifications } = this.beneficialOwner;
    const {
      loading: identificationLoading,
      value,
      type,
    } = (identifications && identifications[0]) || {};
    return (
      <LoadingOverlay active={loading}>
        {this.renderAddIdentificationModal()}
        <Card
          title={this.cardTitle}
          testId={testId}
          controls={editable && (editActive ? this.renderHeaderButtons() : this.renderControl())}
        >
          {this.renderBaseInfo()}
          {this.renderHome()}
          <StoreContext.Consumer>
            {({ ProgramConfigStore = {} }) => {
              return ProgramConfigStore.isV2
                ? this.renderIdentifications()
                : this.renderV1Identification();
            }}
          </StoreContext.Consumer>
        </Card>
      </LoadingOverlay>
    );
  }
}

BeneficialOwnerCard.propTypes = {
  cardTitle: PropTypes.string,
  beneficialOwner: PropTypes.object,
  canRevealIdentification: PropTypes.bool,
  businessStore: PropTypes.any,
  editable: PropTypes.bool,
};

BeneficialOwnerCard.defaultProps = {
  cardTitle: 'Beneficial Owner',
  beneficialOwner: {},
  canRevealIdentification: false,
  businessStore: {},
  editable: false,
};

export default observer(BeneficialOwnerCard);
